import React, { ReactNode, useState } from 'react';
import { AxiosResponse } from 'axios';
import Btn from '../../../components/molecules/Btn';
import { ButtonType } from '../../../types/ButtonType';
import { translatePageType } from '../PageType/PageTypeTitle';
import { Icons, SVGIcon } from '../../../components/atoms/SVG';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { AcceptDeclineForm } from '../../Form2/standardForms/AcceptDeclineForm/AcceptDeclineForm';
import { useAppContext } from '../../../contexts/AppContext/AppContext';
import { PageType } from '../../../types/api/PageType';
import { CheckBoxParam, DateParam, SonForm, TextAreaParam } from '../../Form2';
import { useDelayed } from '../../../hooks/useDelayed';
import useRedirect from '../../../hooks/useRedirect';
import { Hint } from '../Hint';
import { PublishForm } from '../../Form2/standardForms/Publish/PublishForm';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import { RejectProposalForm } from '../../../_blocks/ProposedPagePublicationInfo/forms/RejectProposalForm';

const Actions = [
  'delete-page',
  'unArchive',
  'publish',
  'dePublish',
  'navigationsettings-archive',
  'publishProposal',
  'rejectProposal',
] as const;
type ActionKeys = (typeof Actions)[number];
export type PageManagementActions = {
  [k in ActionKeys]?: {
    embedded: {
      form: SonForm<
        | CheckBoxParam<'pagePublishNonAreaSubPages'>
        | CheckBoxParam<'pagePublishAreaSubPages'>
        | CheckBoxParam<'importantChange'>
        | CheckBoxParam<'triggerTimeControlledPublication'>
        | TextAreaParam<'changeComment'>
        | DateParam<'validUntil'>
        | DateParam<'scheduledDate'>
        | DateParam<'archivedDate'>
      >;
    };
  };
};

const svgSpriteIdMap = new Map<string, SVGIcon>();
svgSpriteIdMap.set('delete-page', Icons.DELETE_TRASHCAN);
svgSpriteIdMap.set('publish', Icons.PUBLISH);
svgSpriteIdMap.set('dePublish', Icons.DEPUBLISH);
svgSpriteIdMap.set('navigationsettings-archive', Icons.ARCHIVE);
svgSpriteIdMap.set('unArchive', Icons.UN_ARCHIVE);
svgSpriteIdMap.set('publishProposal', Icons.PUBLISH);
svgSpriteIdMap.set('rejectProposal', Icons.TIMEBOX_UNSET);

export function PageManagementActions(props: Readonly<PageManagementActions>) {
  const { t } = useSonTranslation('PageManagement');
  const { headMetaInfo } = useAppContext();

  const [startRedirect] = useRedirect();
  const [action, setAction] = useState<ActionKeys | undefined>();
  const delayCloseModal = useDelayed((response: AxiosResponse) => {
    if (response.data.url) {
      startRedirect(response.data.url);
    } else {
      setAction(undefined);
    }
  });

  const getForwardMessage = () => {
    switch (action) {
      case 'delete-page':
      case 'publish':
      case 'dePublish':
      case 'navigationsettings-archive':
      case 'unArchive':
      case 'publishProposal':
        return t(`views.${action}.form.status.successForward`);
      default:
        return undefined;
    }
  };

  const getHint = () => {
    switch (action) {
      case 'delete-page':
        return <Hint message={t(`views.${action}.form.hint`)} />;
      default:
        return null;
    }
  };

  const getHeadline = (actionKey: ActionKeys) => {
    switch (actionKey) {
      case 'delete-page':
        return t(`views.${actionKey}.title`, {
          pageType: translatePageType(headMetaInfo.pageType as PageType),
        });
      case undefined:
        return undefined;
      default:
        return t(`views.${actionKey}.title`);
    }
  };

  const getForm = () => {
    if (!action) {
      return null;
    }

    const form = props[action]?.embedded.form;

    if (form) {
      if (action === 'publish' || action === 'publishProposal') {
        return <PublishForm form={form} onSuccess={delayCloseModal} />;
      }

      if (action === 'rejectProposal') {
        return <RejectProposalForm form={form} onSuccess={delayCloseModal} />;
      }

      return (
        <AcceptDeclineForm
          messageSuccess={t(`views.${action}.form.status.success`)}
          messageForward={getForwardMessage()}
          messageSending={t(`views.${action}.form.status.sending`)}
          messageFail={t(`views.${action}.form.status.fail`)}
          headline={t(`views.${action}.form.question`)}
          form={form}
          onSuccess={delayCloseModal}
        >
          {getHint()}
        </AcceptDeclineForm>
      );
    }

    return null;
  };

  return (
    <div className="page-management-action">
      {Object.keys(props).reduce<ReactNode[]>((accumulator, currentValue) => {
        const tmp = [...accumulator];
        const key = currentValue as ActionKeys;
        if (Actions.includes(key)) {
          const text = getHeadline(key);
          tmp.push(
            <div className="page-management-action__button" key={key}>
              <Btn
                type={ButtonType.BUTTON}
                text={text}
                titleText={text}
                hiddenText
                tooltipPlacement={TooltipPlacement.TOP}
                svgSpriteId={svgSpriteIdMap.get(key)}
                onClick={() => {
                  setAction(key);
                }}
              />
            </div>,
          );
        }

        return tmp;
      }, [])}
      {action
        ? (
            <ModalDialog
              closeModal={() => {
                setAction(undefined);
              }}
              headline={getHeadline(action)}
              initialFocusIndex={1}
            >
              {getForm()}
            </ModalDialog>
          )
        : null}
    </div>
  );
}
